<template>
  <div id="dress" class="article-products" :class="classes">
    <div class="article-products__header">
      <div v-if="subHeading" class="article-products__type">
        {{ subHeading }}
      </div>
      <h3 v-if="heading" class="article-products__heading">{{ heading }}</h3>
      <p v-if="text" class="article-products__text">
        {{ text }}
      </p>
      <div v-else class="article-products__text">
        <slot name="content" />
      </div>
    </div>
    <Carousel v-if="!loading">
      <div v-for="product in products" :key="product.id" class="slide">
        <ProductCardHelper
          v-slot="{ primaryMedia, secondaryMedia }"
          :product="product"
          :image-type="imageType"
        >
          <ProductCard
            :product="product"
            :media="primaryMedia"
            :hover-media="secondaryMedia"
          />
        </ProductCardHelper>
      </div>
    </Carousel>
  </div>
</template>

<script>
import Carousel from "@/components/Carousel.vue";
import ProductCard from "@/components/ProductCard.vue";
import ProductCardHelper from "@/components/product-card/ProductCardHelper.vue";
import collectionQuery from "@/graphql/queries/CollectionV2.gql";
import { unwrapNodes } from "@/helpers/graphql";
import { RelayConnection } from "@/helpers/pagination";
import { mapGetters } from "vuex";

export default {
  name: "ArticleProducts",
  components: {
    Carousel,
    ProductCard,
    ProductCardHelper,
  },
  props: {
    collectionHandle: {
      type: String,
      required: true,
    },
    subHeading: {
      type: String,
      default: null,
    },
    heading: {
      type: String,
      default: null,
    },
    text: {
      type: String,
      default: null,
    },
    imageType: {
      type: String,
      default: "campaignImages",
    },
    headingLeftAligned: {
      type: Boolean,
      default: false,
    },
    block: {
      type: Boolean,
      default: false,
    },
    styleSeries: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      collection: {
        products: new RelayConnection(),
      },
      photoMode: "model",
      loading: true,
    };
  },
  computed: {
    ...mapGetters("collection", ["products"]),
    classes() {
      return {
        "article-products--heading-left-aligned": this.headingLeftAligned,
        "article-products--block": this.block,
        "article-products--styleSeries": this.styleSeries,
      };
    },
    products() {
      return unwrapNodes(this.collection.products?.edges);
    },
  },
  apollo: {
    collection: {
      query: collectionQuery,
      variables() {
        return {
          collection: this.collectionHandle,
          first: 250,
        };
      },
      result() {
        this.loading = false;
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.product-carousel {
  display: flex;
  @include respond-above(md) {
    justify-content: center;
  }
}

.article-products {
  margin: 0 0 60px 0;

  @include respond-above(md) {
    max-width: 1600px;
    margin: 0 auto 80px;
    padding: 0 20px 0;
  }

  &--block {
    margin: 60px 0 60px;
    @include respond-above(md) {
      margin: 80px 0 80px;
    }
  }

  &--styleSeries {
    display: flex;
    .article-products__header {
      text-align: left;
    }
    .article-products__heading {
      @include editorial-headline(20px);

      @include respond-above(md) {
        @include editorial-headline(28px);
      }
    }
    .article-products__text {
      @include editorial-body(14px);
    }
  }

  &__product-carousel {
    display: flex;
  }

  &__header {
    padding: 0 20px;
    text-align: center;
  }

  &__type {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.55px;
    margin: 0 0 10px;
    text-transform: uppercase;
  }

  &__heading {
    color: $color-grey-dark;
    font-size: 18px;
    font-weight: 700;
    margin: 0 0 10px 0;
    text-transform: uppercase;
    @include respond-above(md) {
      font-size: 24px;
    }
  }

  &__text {
    font-family: $utility-font-family;
    font-size: 16px;
    font-weight: 300;
    margin: 0 auto 40px;
    max-width: 610px;

    @include respond-above(md) {
      font-size: 18px;
    }
    :slotted(span) {
      display: block;
    }

    :slotted(p) {
      font-size: 16px;
      @include respond-above(md) {
        font-size: 18px;
      }
    }
  }
  &--heading-left-aligned {
    .article-products {
      &__header {
        text-align: left;
        @include respond-above(md) {
          margin: 80px 0 0;
        }
      }
      &__heading {
        font-size: 24px;
        font-weight: 400;
        text-transform: none;
      }
      &__text {
        font-size: 12px;
        font-weight: 400;
        margin: 0 0 40px;
      }
    }
  }
}
</style>
